/* „Program", the create window's first step — canvas rows H and I.
 *
 * The WINDOW is `shared/ui/splitDialog.module.css`, a chosen row is `shared/ui/choiceRow`, the facet
 * is `shared/ui/segmented` and the search is `shared/ui/SearchField`; nothing any of them states is
 * restated here. A declaration written on both sides is settled by chunk order, which differs
 * between the dev server and the build.
 *
 * Every colour, space, radius and type value is a token. Widths, heights and grid tracks are layout
 * and are deliberately outside the gate. */

/* ── The left half ──────────────────────────────────────────────────────────────────────────── */

/* The whole column, as the one `fieldset` the start's radios share. It takes the form pane's
 * height, so the list at its foot is the part that grows and scrolls. */
.program {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  min-width: var(--space-0);
  min-height: var(--space-0);
  margin: var(--space-0);
  padding: var(--space-0);
  border: 0;
}

/* „Zacznij od zera": a dashed box while it is only an option. Chosen, it wears the choice row's own
 * `.rowSelected` — the wash, the edge and the radio dot — and this sheet only turns its dashed
 * border solid in the accent. The border's ink is a custom property because the value gate reads
 * real property names only. */
.scratch {
  --scratch-ink: var(--border-2);

  position: relative;
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-3);
  border: var(--border-thick) dashed var(--scratch-ink);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.scratchOn {
  --scratch-ink: var(--accent);

  border-style: solid;
}

.scratch:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* The „+" tile. Neutral: the row's own radio is what marks it chosen. */
.plus {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  font-size: var(--fs-h3);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-none);
  color: var(--text);
  background: var(--bg-chip);
  border-radius: var(--radius-8px);
}

/* The eyebrow's type — worn by the divider's words, the pinned row's name and the preview's label. */
.eyebrow {
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-eyebrow);
  color: var(--text-2);
  text-transform: uppercase;
}

/* „albo skopiuj szablon", between two hairlines, in the eyebrow's type. */
.divider {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  margin: var(--space-0);
}

.divider::before,
.divider::after {
  flex: 1;
  height: 1px;
  content: '';
  background: var(--border);
}

.searchRow {
  display: flex;
}

/* The reserve: the rest of the column, never less than three rows, scrolling inside itself. */
.list {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 150px;
  overflow-y: auto;
}

.skeletonRow {
  flex: none;
  height: var(--h-assign-row);
  margin-bottom: var(--space-2px);
}

/* What stands in the answered reserve: the pinned choice, then the matches or the no-match state. */
.listBody {
  display: flex;
  flex: 1;
  flex-direction: column;
}

/* The chosen template the filters leave out, named as such and ruled off from what they leave. */
.pinned {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-6px);
  padding-bottom: var(--space-3);
  margin-bottom: var(--space-3);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* One template: its thumbnail and its words, in the choice row's middle track. */
.templateBody {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  min-width: var(--space-0);
}

/* The width and height are the image's reserve, stated before it loads. The fit and the ground
 * are `shared/ui/photoBox`'s. */
.thumb {
  flex: none;
  width: 58px;
  height: 38px;
  border-radius: var(--radius-7px);
}

.templateName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* A failed or empty list, centred in the reserve it stands in. */
.state {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  text-align: center;
}

.stateText {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* ── The right half ─────────────────────────────────────────────────────────────────────────── */

.preview {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
}

/* The empty plan: five dashed day slots on the sunken ground. */
.emptyPlan {
  display: flex;
  gap: var(--space-10px);
  align-items: center;
  justify-content: center;
  height: 220px;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.emptySlot {
  width: 52px;
  height: 140px;
  border: var(--border-thick) dashed var(--border-2);
  border-radius: var(--radius-8px);
}

/* The template's cover, at the width of the pane. The height is its reserve; the fit and the
 * ground are `shared/ui/photoBox`'s. */
.hero {
  width: 100%;
  height: 132px;
  border-radius: var(--radius-12px);
}

.previewTitle {
  margin: var(--space-0);
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  color: var(--text);
}

.previewBody {
  max-width: 52ch;
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

/* The week's read failing, in the week's own box — `--h-pl-week`, the section's reserve. */
.weekFailure {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  align-items: center;
  justify-content: center;
  height: var(--h-pl-week);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-9-5px);
}
